import { uniq, compact, values } from 'lodash-es'
import { getCachedWdUriFromWikimediaLanguageCode, getWikimediaLanguageCodesFromWdUris } from '#app/lib/languages/languages_indexes'
import { arrayIncludes, objectValues } from '#app/lib/utils'
import { getEntityLangUris } from '#entities/components/lib/claims_helpers'
import { getEntitiesAttributesByUris, getPublicationYear } from '#entities/lib/entities'
import type { SerializedEntity } from '#entities/lib/entities'
import type { EntityUri, EntityValue, WdEntityUri } from '#server/types/entity'
import { getCurrentLang } from '#user/lib/i18n'
import type { WikimediaLanguageCode } from 'wikibase-sdk'

export interface WorkEditionFilters {
  selectedLang?: string
  selectedLangLabel?: string
  selectedPublicationYear?: string
  selectedPublisher?: EntityValue | 'unknown' | 'all'
  selectedPublisherLabel?: string
}

async function fetchEntitiesLabels (uris: EntityUri[]) {
  const { entities } = await getEntitiesAttributesByUris({
    uris,
    attributes: [ 'labels' ],
    lang: getCurrentLang(),
  })
  return entities
}

function prioritizeMainUserLang (langs: WikimediaLanguageCode[]) {
  const userLang = getCurrentLang()
  if (langs.includes(userLang)) {
    const userLangIndex = langs.indexOf(userLang)
    langs.splice(userLangIndex, 1)
    langs.unshift(userLang)
  }
  return langs
}

export type LangEntitiesLabel = Partial<Record<WikimediaLanguageCode, string>>

export async function getLangEntities (editions: SerializedEntity[]) {
  const editionsLanguagesUris = compact(uniq(editions.flatMap(getEntityLangUris))) as WdEntityUri[]
  const editionsLangsByUris = await getWikimediaLanguageCodesFromWdUris(editionsLanguagesUris)
  const rawEditionsLangs = values(editionsLangsByUris)
  const editionsLangs = prioritizeMainUserLang(rawEditionsLangs)
  // Not using getLanguagesLabels as it might include languages without a Wikimedia language code
  const entities = await fetchEntitiesLabels(editionsLanguagesUris)
  const langEntitiesLabel: LangEntitiesLabel = {}
  editionsLangs.forEach(lang => {
    const languageUri = getCachedWdUriFromWikimediaLanguageCode(lang)
    if (languageUri && entities[languageUri]) {
      langEntitiesLabel[lang] = objectValues(entities[languageUri].labels)[0]
    }
  })
  return { editionsLangs, langEntitiesLabel }
}

export type PublishersLabels = Partial<Record<EntityUri, string>>

export async function getPublishersEntities (editions: SerializedEntity[]) {
  const rawPublishersUris = uniq(editions.flatMap(getPublisherUri))
  const publishersUris = compact(rawPublishersUris)
  const someEditionsHaveNoPublisher = rawPublishersUris.length !== publishersUris.length
  const publishers = await fetchEntitiesLabels(publishersUris)
  const publishersLabels: PublishersLabels = {}
  objectValues(publishers).forEach(publisher => {
    publishersLabels[publisher.uri] = objectValues(publisher.labels)[0]
  })
  return { publishersUris, publishersLabels, someEditionsHaveNoPublisher }
}

const getPublisherUri = (edition: SerializedEntity) => edition.claims['wdt:P123']

export const hasPublisher = (publisherUri: WorkEditionFilters['selectedPublisher']) => (edition: SerializedEntity) => {
  if (publisherUri === 'unknown') {
    return edition.claims['wdt:P123'] == null
  } else {
    return edition.claims['wdt:P123'] && arrayIncludes(edition.claims['wdt:P123'], publisherUri)
  }
}

export function getPublicationYears (editions: SerializedEntity[]) {
  const years = editions.map(getPublicationYear).flat()
  const someEditionsHaveNoPublicationDate = compact(years).length !== years.length
  return {
    publicationYears: uniq(compact(years)).sort(antiChronologically),
    someEditionsHaveNoPublicationDate,
  }
}

const antiChronologically = (a, b) => parseInt(b) - parseInt(a)

export const hasPublicationYear = (year: string) => (edition: SerializedEntity) => {
  if (year === 'unknown') {
    return getPublicationYear(edition) == null
  } else {
    return getPublicationYear(edition) === year
  }
}
